import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, ArrowRight, ArrowLeft } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { cn } from "../lib/utils";
import { useSavePrefs } from "../hooks/useProfile";

type Gender = "male" | "female" | "diverse";

const GENDERS: { value: Gender; label: string }[] = [
  { value: "male", label: "Männer" },
  { value: "female", label: "Frauen" },
  { value: "diverse", label: "Divers" },
];

type Radius = 10 | 25 | 50 | 100 | -1;
const RADIUS_OPTIONS: Array<{ value: Radius; label: string }> = [
  { value: 10, label: "10 km" },
  { value: 25, label: "25 km" },
  { value: 50, label: "50 km" },
  { value: 100, label: "100 km" },
  { value: -1, label: "Deutschlandweit" },
];

export function OnboardingSuche() {
  const [wantGenders, setWantGenders] = useState<Gender[]>([]);
  const [ageRange, setAgeRange] = useState<[number, number]>([24, 36]);
  const [radius, setRadius] = useState<Radius>(25);
  const [error, setError] = useState<string | null>(null);
  const save = useSavePrefs();
  const navigate = useNavigate();

  function toggleGender(g: Gender) {
    setWantGenders((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g],
    );
  }

  const canContinue =
    wantGenders.length > 0 &&
    ageRange[0] >= 18 &&
    ageRange[1] >= ageRange[0];

  async function handleFinish() {
    setError(null);
    try {
      await save.mutateAsync({
        wantGenders,
        ageMin: ageRange[0],
        ageMax: ageRange[1],
        radiusKm: radius,
      });
      navigate("/warteliste");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    }
  }

  return (
    <AppShell hideNav>
      <section className="container max-w-2xl py-10">
        <div className="mb-8 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
            <Compass className="h-5 w-5 text-white" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-500">
              Schritt 4 von 4
            </p>
            <h1 className="text-2xl font-bold tracking-tight">Wen suchst du?</h1>
          </div>
        </div>

        <div className="card space-y-8 p-6 md:p-8">
          {error && (
            <div
              role="alert"
              className="rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-700"
            >
              {error}
            </div>
          )}

          <div>
            <h2 className="text-sm font-semibold text-ink-900">Geschlecht</h2>
            <p className="mt-1 text-sm text-ink-500">
              Du kannst mehrere auswählen.
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {GENDERS.map(({ value, label }) => {
                const active = wantGenders.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleGender(value)}
                    className={cn(
                      "rounded-full border px-4 py-3 text-sm font-semibold transition active:scale-[0.98]",
                      active
                        ? "border-transparent bg-brand-gradient text-white shadow-glow"
                        : "border-border bg-white text-ink-700 hover:border-ink-300",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-ink-900">Alter</h2>
              <span className="text-sm font-bold tabular-nums text-brand-500">
                {ageRange[0]} – {ageRange[1]} Jahre
              </span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">
                  Minimum
                </span>
                <input
                  type="range"
                  min={18}
                  max={80}
                  value={ageRange[0]}
                  onChange={(e) =>
                    setAgeRange(([_, hi]) => [Number(e.target.value), hi])
                  }
                  className="mt-2 w-full accent-brand-500"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">
                  Maximum
                </span>
                <input
                  type="range"
                  min={18}
                  max={80}
                  value={ageRange[1]}
                  onChange={(e) =>
                    setAgeRange(([lo, _]) => [lo, Number(e.target.value)])
                  }
                  className="mt-2 w-full accent-brand-500"
                />
              </label>
            </div>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-ink-900">Entfernung</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {RADIUS_OPTIONS.map(({ value, label }) => {
                const active = radius === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRadius(value)}
                    className={cn(
                      "rounded-full border px-4 py-2 text-sm font-semibold transition active:scale-[0.98]",
                      active
                        ? "border-transparent bg-brand-gradient text-white shadow-glow"
                        : "border-border bg-white text-ink-700 hover:border-ink-300",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => navigate("/onboarding/werte")}
              className="btn-secondary"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </button>
            <button
              type="button"
              onClick={handleFinish}
              disabled={!canContinue || save.isPending}
              className="btn-primary disabled:opacity-50"
            >
              {save.isPending ? "Speichern …" : "Fertigstellen"}
              {!save.isPending && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </section>
    </AppShell>
  );
}
